import { useEffect, useState } from "react";
import { PasswordLessLogin } from "../SignupPage/LoginStep/PasswordLessLogin";
import { OTPCodeStep } from "../SignupPage/LoginStep/OTPCodeStep";
import { trackEvent } from "@/utils/analytics";
import { AnalyticsEventType } from "@/constants/analytics";
import { LOGIN_OPTIONS } from "../SignupPage/LoginStep/LoginStep";
import { useAuthActions, useAuthState } from "../../store/auth";
import { SignupErrorFeedback } from "../SignupPage/SignupErrorFeedback/SignupErrorFeedback";
import { ucfirst } from "@/utils/string";
import { cn } from "@/utils/styles";
import { Loader } from "../Common/Loader/Loader";
import { useLocation } from "wouter";
import { Separator } from "../ui/separator";
import localCookies from "@/utils/cookie";
import { useQueryParams } from "@/hooks/useQueryParams";
import { sanitizeRedirectUrl } from "@/utils/sanitize";

type LoginContentProps = {
  isOpen?: boolean;
  isSignUp?: boolean;
  className?: string;
  onChangeIsSignUp?: (isSignUp: boolean) => void;
};

export const LoginContent = ({
  isOpen,
  isSignUp,
  className,
  onChangeIsSignUp
}: LoginContentProps) => {
  const query = useQueryParams<{ r: string }>();
  const [location] = useLocation();
  const [isSubmitting, setSubmitting] = useState(false);
  const [isOtpOpen, setIsOtpOpen] = useState(false);
  const [isDeprecatedOpen, setIsDeprecatedOpen] = useState(false);
  const { beginSso, completeOtpLogin, clearEmailVerificationRequired } = useAuthActions();
  const failedError = useAuthState((state) => state.error);
  const ssoType = useAuthState((state) => state.ssoType);
  const emailVerificationRequired = useAuthState((state) => state.emailVerificationRequired);
  const getRedirectUrl = () => {
    const raw = query.r ?? location;
    return sanitizeRedirectUrl(raw);
  };

  useEffect(() => {
    const pageVisibilityHandler = () => {
      setSubmitting(false);
    };

    window.addEventListener("pagehide", pageVisibilityHandler, false);
    return () => {
      window.removeEventListener("pagehide", pageVisibilityHandler);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setSubmitting(false);
      setIsOtpOpen(false);
      setIsDeprecatedOpen(false);
    }
  }, [isOpen]);

  // Show OTP screen when email verification is required
  const showEmailVerificationOTP =
    emailVerificationRequired !== null && emailVerificationRequired !== undefined;

  return (
    <div className={cn("flex flex-col gap-7 p-7 pb-5 pt-0 md:max-w-[420px]", className)}>
      {failedError && <SignupErrorFeedback>{ucfirst(failedError)}</SignupErrorFeedback>}

      <div className="flex flex-col">
        {showEmailVerificationOTP && emailVerificationRequired && (
          <div className="mt-3 flex flex-col gap-3">
            <OTPCodeStep
              email={emailVerificationRequired.email}
              hasBackButton={true}
              verificationType="email_verification"
              pendingAuthenticationToken={emailVerificationRequired.pending_authentication_token}
              emailVerificationId={emailVerificationRequired.email_verification_id}
              onEmailVerificationComplete={async (response) => {
                // Complete the login flow
                await completeOtpLogin(response.accessToken, response.is_new_user);
                clearEmailVerificationRequired();
              }}
              onCancel={() => {
                clearEmailVerificationRequired();
                // Clear the email verification state and show login options
              }}
            />
          </div>
        )}

        {!isOtpOpen && !showEmailVerificationOTP && (
          <div className="flex flex-col gap-2.5">
            {LOGIN_OPTIONS.map((option) => {
              const { value, label, deprecated } = option;
              const className = ["otp", "other"].includes(value) ? "col-span-2" : "";

              if (value === "otp" && isOtpOpen) {
                return;
              }
              if (!isDeprecatedOpen && deprecated) {
                return;
              }
              if (value === "other" && isDeprecatedOpen) {
                return;
              }

              return (
                <div className={cn("flex flex-col gap-2.5", className)} key={value}>
                  {value === "otp" && (
                    <Separator className="my-3 flex flex-row items-center justify-center">
                      <div className="flex flex-row items-center justify-center bg-background px-2 text-sm text-gray-dark-2200">
                        Or
                      </div>
                    </Separator>
                  )}
                  <a
                    className={cn(
                      `group flex flex-row items-center justify-center gap-3 rounded border border-border bg-gray-dark-600 px-3 py-[7px] text-base !text-gray-dark-2200 transition-all hover:border-border hover:bg-white hover:!text-black dark:border-gray-dark-1100 dark:hover:border-white`,
                      isSubmitting ? `pointer-events-none` : `cursor-pointer`
                    )}
                    onClick={async () => {
                      if (value === "otp") {
                        setIsOtpOpen(true);
                        setIsDeprecatedOpen(false);
                        return;
                      }
                      if (value === "other") {
                        setIsDeprecatedOpen(true);
                        return;
                      }

                      trackEvent(
                        isSignUp
                          ? AnalyticsEventType.SIGN_UP_INITIATED
                          : AnalyticsEventType.SIGNED_IN,
                        {
                          type: value
                        }
                      );
                      window.dataLayer?.push({
                        event: isSignUp
                          ? AnalyticsEventType.SIGN_UP_INITIATED
                          : AnalyticsEventType.SIGNED_IN,
                        type: value
                      });
                      setSubmitting(true);

                      // Preserve an intentional post-login destination (e.g. Account Settings
                      // from Manage keys) instead of overwriting with the current page.
                      const existingRedirect = localCookies.getItem("LOGIN_REDIRECT_ROUTE");
                      localCookies.setItem(
                        "LOGIN_REDIRECT_ROUTE",
                        sanitizeRedirectUrl(existingRedirect || getRedirectUrl())
                      );

                      try {
                        await beginSso(value);
                      } catch {
                        setSubmitting(false);
                      }
                    }}
                  >
                    {option.icon && (
                      <option.icon className="h-[20px] text-foreground group-hover:text-black" />
                    )}
                    {value === "otp" ? `${isSignUp ? "Sign up" : "Login"} with ${label}` : label}
                    {isSubmitting && value === ssoType && (
                      <Loader className="!h-[14px] !w-[14px]" color="#fff" />
                    )}
                  </a>
                </div>
              );
            })}
          </div>
        )}

        {isOtpOpen && (
          <div className="mt-3 flex flex-col gap-3">
            <PasswordLessLogin
              hasBackButton={true}
              onCancel={() => {
                setIsOtpOpen(false);
              }}
            />
          </div>
        )}
      </div>

      {isSignUp ? (
        <p className="flex flex-row items-center text-sm text-gray-dark-2200">
          <span>Already a Member?</span>

          <div className="flex-1"></div>

          <a
            onClick={() => {
              onChangeIsSignUp?.(false);
            }}
            className="cursor-pointer text-foreground underline"
          >
            Login
          </a>
        </p>
      ) : (
        <p className="flex flex-row items-center text-sm text-gray-dark-2200">
          <span>Don't have an account?</span>

          <div className="flex-1"></div>

          <a
            onClick={() => {
              trackEvent(AnalyticsEventType.SIGN_UP_CLICKED);
              onChangeIsSignUp?.(true);
            }}
            className="cursor-pointer text-foreground underline"
          >
            Sign Up
          </a>
        </p>
      )}
    </div>
  );
};
